import * as React from "react";
import { FunnelContainer } from "ui/funnelContainer/FunnelContainer";
import { CollectFunnelContext } from "components/collectFunnel/CollectFunnelProvider";
import { Calendar } from "components/calendar/Calendar";
import { getUnixTimeFromDate } from "helpers/getUnixTimeFromDate";
import moment from "moment";
import { SET_TIMESLOT } from "components/collectFunnel/constants";
import { useRouter } from "next/router";
import { Typography, makeStyles, Box } from "@material-ui/core";
import { Grid } from "ui/grid/Grid";
import { useQuery } from "react-query";
import { pushCollectionSlotOptions, pushCollectionSlotSelected } from "lib/gtm";
import { getDelay } from "helpers/date";
import { STEP_ACCOUNT_URL, STEP_PRODUCT_CHOICE_URL } from "../../constants";

const getTimeslotsToDisplay = (timeslots, date) => {
  const nextTimeslot =
    timeslots.filter((timeslot) => moment(timeslot.time_span_end) > moment(date).add(2, "days"))[0] || timeslots[0];
  const timeslotsToDisplay = timeslots.filter((timeslot) =>
    moment(timeslot.time_span_beginning).isBetween(
      moment(date).format("YYYY-MM-DD"),
      moment(date).add(2, "days").format("YYYY-MM-DD"),
      "days",
      "[]"
    )
  );
  return { timeslotsToDisplay, nextTimeslot };
};

const formatTimeslotData = (timeslotsToDisplay, nextTimeslot, status) => {
  return {
    detail: status === "error" && "error",
    next_timeslot: {
      beginning: nextTimeslot?.time_span_beginning,
      end: nextTimeslot?.time_span_end,
      id: nextTimeslot?.id,
    },
    timeslots: timeslotsToDisplay?.map((timeslot) => ({
      ...timeslot,
      id: Math.floor(Math.random() * 9999),
      beginning: timeslot.time_span_beginning,
      end: timeslot.time_span_end,
    })),
  };
};

export const CollectFunnelDateStep = () => {
  const [state, dispatch] = React.useContext(CollectFunnelContext);
  const classes = useStyles();
  const [fetchQueryParams] = React.useState({
    start_date: moment().format("YYYY-MM-DD"),
    end_date: moment().add(20, "days").format("YYYY-MM-DD"),
    postal_code: state.postalCode,
    delivery_type: "collection",
  });
  const [queryParams, setQueryParams] = React.useState({
    beginning: getUnixTimeFromDate(),
    company: process.env.NEXT_PUBLIC_COMPANY_ID,
    end: getUnixTimeFromDate(moment().add(2, "days")),
    postal_code: state.postalCode,
  });
  const [timeslotData, setTimeslotData] = React.useState(null);
  const router = useRouter();
  const shouldDisableContinueButton = React.useMemo(() => {
    const allInfoFilled = state.address && state.timeslot;
    return !allInfoFilled;
  }, [state]);

  const trackedSlotOptions = React.useRef(false);

  React.useEffect(() => {
    if (trackedSlotOptions.current) {
      return;
    }
    if (timeslotData?.timeslots?.length > 0) {
      pushCollectionSlotOptions(
        state.postalCode,
        timeslotData.timeslots.length,
        getDelay(moment().format("YYYY-MM-DD"), timeslotData.timeslots[0].beginning)
      );
      trackedSlotOptions.current = true;
    }
  }, [timeslotData, state.postalCode]);

  const { data, status } = useQuery(["timeslot/shipping-timeslots", fetchQueryParams], {
    onSuccess: (data) => {
      if (data.length > 0) {
        const { timeslotsToDisplay, nextTimeslot } = getTimeslotsToDisplay(data);
        setTimeslotData(formatTimeslotData(timeslotsToDisplay, nextTimeslot, status));
      } else {
        setTimeslotData({ timeslots: [] });
        pushCollectionSlotOptions(state.postalCode, 0, -1);
      }
    },
  });

  const handleNavigate = (date) => {
    const { timeslotsToDisplay, nextTimeslot } = getTimeslotsToDisplay(data, date);
    setTimeslotData(formatTimeslotData(timeslotsToDisplay, nextTimeslot, status));
    setQueryParams({
      beginning: getUnixTimeFromDate(date),
      end: getUnixTimeFromDate(date.add(2, "d")),
      postal_code: state.postalCode,
    });
  };
  const handleNextStep = React.useCallback(() => {
    router.push(STEP_ACCOUNT_URL, undefined, { shallow: true });
  }, [router]);

  const handlePreviousStep = React.useCallback(() => {
    router.push(STEP_PRODUCT_CHOICE_URL, undefined, { shallow: true });
  }, [router]);

  const setTimeslot = (slot) => {
    dispatch({
      type: SET_TIMESLOT,
      data: { timeslot: slot },
    });
    pushCollectionSlotSelected(
      state.postalCode,
      moment(slot.time_span_beginning).format("HH:mm"),
      moment(slot.time_span_beginning).format("YYYY-MM-DD")
    );
  };

  return (
    <FunnelContainer>
      <div className={classes.container}>
        <Grid container>
          <Box pl={3} mb={3}>
            <Typography color="secondary" variant="h2" className={classes.header}>
              Choisissez le jour et la plage horaire de votre rendez-vous
            </Typography>
          </Box>
          <Calendar
            setTimeslot={setTimeslot}
            selectedTimeslot={state.timeslot}
            shouldDisableContinueButton={shouldDisableContinueButton}
            data={data && timeslotData}
            queryParams={queryParams}
            handleNextStep={handleNextStep}
            handlePreviousStep={handlePreviousStep}
            onPrevious={handleNavigate}
            onNext={handleNavigate}
            checkItems={["Récupération gratuite", "Créneau de 5h", "Collecte dans la pièce de votre choix"]}
            timeslotText="Offrez une seconde vie à votre appareil"
            checkItemsTitle="Reprise de votre appareil"
          />
        </Grid>
      </div>
    </FunnelContainer>
  );
};

const useStyles = makeStyles((theme) => ({
  container: {
    position: "relative",
    padding: "40px 24px",
    [theme.breakpoints.down("sm")]: {
      padding: 24,
    },
  },
  cardContainer: {
    padding: theme.spacing(5),
    marginBottom: theme.spacing(5),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(3),
    },
  },
  leftBox: {
    width: "60%",
    marginRight: theme.spacing(8),
    [theme.breakpoints.down("sm")]: {
      marginRight: 0,
      width: "100%",
    },
  },
  select: {
    height: theme.spacing(7),
    background: "white",
    border: "1px solid rgba(219, 223, 232, 0.6)",
    boxSizing: "border-box",
    borderRadius: 12,
  },
  roundedInformation: {
    border: "1px solid #D7D7D7",
    boxSizing: "border-box",
    borderRadius: theme.spacing(3),
    display: "grid",
    gridAutoFlow: "column",
    gridColumnGap: theme.spacing(2),
    padding: theme.spacing(3),
    marginTop: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      marginTop: theme.spacing(1),
    },
  },
  circle: {
    minWidth: theme.spacing(5),
  },
  listTitle: {
    paddingLeft: theme.spacing(4),
    fontWeight: "bold",
    fontSize: 16,
    lineHeight: "120%",
    textTransform: "uppercase",
    color: theme.palette.secondary.dark,
    paddingTop: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      marginTop: theme.spacing(2),
    },
  },
}));
